import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  ArrowRight,
  BookOpenCheck,
  CheckCircle2,
  ExternalLink,
  Flag,
  GraduationCap,
  Lightbulb,
  PlayCircle,
} from "lucide-react";
import { toast } from "sonner";
import StudyShell from "@/components/study/StudyShell";
import StudyRunner from "@/components/study/StudyRunner";
import { getLessonById, LEARN_CURRICULUM } from "@/data/learnCurriculum";
import { getLearningProgress, trackEvent, updateLearningProgress } from "@/lib/api";
import { STUDY } from "@/constants/testIds";
import { useStudyAuth } from "@/context/StudyAuth";
import UpgradeGate from "@/components/study/UpgradeGate";

const FREE_LESSON_IDS = new Set(["illinois-licensing", "illinois-license-act"]);

function LessonNotFound() {
  return (
    <StudyShell testId={STUDY.lessonPage}>
      <div className="border border-[color:rgba(10,27,51,0.12)] bg-white p-8 text-center">
        <h1 className="text-xl font-bold text-[var(--ibp-ink)]">Lesson not found</h1>
        <p className="mt-2 text-sm text-[color:rgba(11,18,32,0.65)]">
          This lesson may have moved as the learning path grows.
        </p>
        <Link
          to="/study/learn"
          className="mt-5 inline-flex min-h-10 items-center gap-2 bg-[var(--ibp-navy-950)] px-4 py-2 text-sm font-semibold text-white"
        >
          <ArrowLeft className="h-4 w-4" /> Back to Learn
        </Link>
      </div>
    </StudyShell>
  );
}

export default function StudyLesson() {
  const { lessonId } = useParams();
  const { hasFullAccess } = useStudyAuth();
  const lesson = getLessonById(lessonId);
  const queryClient = useQueryClient();
  const [checkpointOpen, setCheckpointOpen] = useState(false);
  const { data: progress } = useQuery({
    queryKey: ["learning-progress"],
    queryFn: getLearningProgress,
  });
  const complete = (progress?.completed_ids || []).includes(lessonId);
  const currentIndex = LEARN_CURRICULUM.findIndex((item) => item.id === lessonId);
  const nextLesson = currentIndex >= 0 ? LEARN_CURRICULUM[currentIndex + 1] : null;

  useEffect(() => {
    if (!lesson) return;
    trackEvent("lesson_opened", {
      lesson_id: lesson.id,
      outline_section: lesson.outlineSection,
    });
  }, [lesson]);

  const progressMutation = useMutation({
    mutationFn: ({ completed }) => updateLearningProgress(lessonId, completed),
    onSuccess: (_, { completed }) => {
      queryClient.invalidateQueries({ queryKey: ["learning-progress"] });
      if (completed) {
        trackEvent("lesson_completed", {
          lesson_id: lessonId,
          outline_section: lesson?.outlineSection,
        });
        toast.success("Lesson marked complete");
      }
    },
    onError: () => toast.error("We could not save that lesson progress. Please try again."),
  });

  const terms = useMemo(() => lesson?.terms || [], [lesson]);

  if (!lesson) return <LessonNotFound />;
  if (!hasFullAccess && !FREE_LESSON_IDS.has(lesson.id)) {
    return (
      <StudyShell testId={STUDY.lessonPage}>
        <UpgradeGate
          eyebrow={`Lesson ${lesson.number}`}
          title={`Unlock “${lesson.title}” and the complete learning path.`}
          description="Your first two Illinois lessons are free. Full access opens every Illinois, national, and math lesson plus its live-question checkpoint."
        />
      </StudyShell>
    );
  }

  return (
    <StudyShell testId={STUDY.lessonPage}>
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[color:rgba(10,27,51,0.12)] pb-5">
        <Link
          to="/study/learn"
          className="inline-flex items-center gap-2 text-sm font-semibold text-[var(--ibp-navy-950)] hover:underline"
        >
          <ArrowLeft className="h-4 w-4" /> Back to Learn
        </Link>
        <span className="text-xs font-semibold uppercase tracking-wide text-[color:rgba(11,18,32,0.5)]">
          Lesson {lesson.number} of {LEARN_CURRICULUM.length}
        </span>
      </div>

      <header className="border-b border-[color:rgba(10,27,51,0.12)] py-8">
        <p className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-[var(--ibp-gold-600,#b8860b)]">
          <GraduationCap className="h-4 w-4" /> {lesson.category}
        </p>
        <h1 className="mt-3 max-w-3xl text-3xl font-bold leading-tight text-[var(--ibp-ink)]">{lesson.title}</h1>
        <p className="mt-3 max-w-3xl text-base leading-7 text-[color:rgba(11,18,32,0.7)]">{lesson.summary}</p>
        <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm">
          <span className="font-medium text-[color:rgba(11,18,32,0.65)]">Active bank topic: {lesson.outlineSection}</span>
          <a
            href={lesson.sourceUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 font-semibold text-[var(--ibp-navy-950)] hover:underline"
          >
            Source: {lesson.sourceReference} <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </div>
      </header>

      <section className="py-8">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-[var(--ibp-gold-600,#b8860b)]">What you should know</h2>
        <ul className="mt-4 grid gap-3 sm:grid-cols-3">
          {lesson.objectives.map((objective) => (
            <li key={objective} className="border-l-2 border-l-[var(--ibp-gold-500)] bg-white px-4 py-3 text-sm leading-6 text-[var(--ibp-ink)]">
              {objective}
            </li>
          ))}
        </ul>
      </section>

      <section className="space-y-8 border-t border-[color:rgba(10,27,51,0.12)] py-8">
        {lesson.blocks.map((block) => (
          <article key={block.heading}>
            <h2 className="text-xl font-bold text-[var(--ibp-ink)]">{block.heading}</h2>
            <p className="mt-3 max-w-3xl text-base leading-7 text-[color:rgba(11,18,32,0.73)]">{block.body}</p>
          </article>
        ))}
      </section>

      <section className="border-y border-[color:rgba(10,27,51,0.12)] bg-[var(--ibp-navy-950)] px-5 py-6 text-white sm:px-6">
        <p className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-[var(--ibp-gold-500)]">
          <Lightbulb className="h-4 w-4" /> {lesson.scenario.title}
        </p>
        <p className="mt-3 max-w-3xl text-base leading-7 text-white/82">{lesson.scenario.body}</p>
      </section>

      <section className="py-8">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-[var(--ibp-gold-600,#b8860b)]">Key terms</p>
            <h2 className="mt-1 text-xl font-bold text-[var(--ibp-ink)]">Make the vocabulary familiar</h2>
          </div>
          <Link to="/study/flashcards" className="text-sm font-semibold text-[var(--ibp-navy-950)] hover:underline">
            Open Word Bank
          </Link>
        </div>
        <div className="mt-4 grid gap-px border border-[color:rgba(10,27,51,0.12)] bg-[color:rgba(10,27,51,0.12)] sm:grid-cols-3">
          {terms.map(([term, definition]) => (
            <div key={term} className="bg-white px-4 py-4">
              <h3 className="text-sm font-bold text-[var(--ibp-ink)]">{term}</h3>
              <p className="mt-2 text-sm leading-6 text-[color:rgba(11,18,32,0.65)]">{definition}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="border-t border-[color:rgba(10,27,51,0.12)] py-8">
        <div className="flex flex-col gap-4 border-l-4 border-l-[var(--ibp-gold-500)] bg-white px-5 py-5 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-[var(--ibp-gold-600,#b8860b)]">
              <BookOpenCheck className="h-4 w-4" /> Real-bank checkpoint
            </p>
            <h2 className="mt-2 text-lg font-bold text-[var(--ibp-ink)]">Test this topic with five live questions.</h2>
            <p className="mt-1 max-w-2xl text-sm leading-6 text-[color:rgba(11,18,32,0.65)]">
              These are active questions from the {lesson.outlineSection} section, with the same explanations and source references used throughout your account.
            </p>
          </div>
          <button
            type="button"
            data-testid={STUDY.lessonCheckpoint}
            onClick={() => setCheckpointOpen((open) => !open)}
            className="inline-flex min-h-10 shrink-0 items-center justify-center gap-2 bg-[var(--ibp-navy-950)] px-4 py-2 text-sm font-semibold text-white"
          >
            <PlayCircle className="h-4 w-4" /> {checkpointOpen ? "Hide checkpoint" : "Start checkpoint"}
          </button>
        </div>
        {checkpointOpen ? (
          <div className="mt-5">
            <StudyRunner
              mode="topic"
              topic={lesson.outlineSection}
              limit={5}
              emptyText="This lesson's active checkpoint is being built. Try the full Question Bank for now."
            />
          </div>
        ) : null}
      </section>

      <section className="border-t border-[color:rgba(10,27,51,0.12)] py-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-lg font-bold text-[var(--ibp-ink)]">
              {complete ? "Lesson complete" : "Ready to move on?"}
            </h2>
            <p className="mt-1 text-sm leading-6 text-[color:rgba(11,18,32,0.65)]">
              {complete
                ? "Your learning-path progress is saved to this account."
                : "Mark this lesson complete when the rule, terms, and checkpoint feel familiar."}
            </p>
          </div>
          <button
            type="button"
            data-testid={STUDY.lessonComplete}
            disabled={progressMutation.isPending}
            onClick={() => progressMutation.mutate({ completed: !complete })}
            className={[
              "inline-flex min-h-10 shrink-0 items-center justify-center gap-2 px-4 py-2 text-sm font-semibold disabled:opacity-60",
              complete
                ? "border border-[color:rgba(10,27,51,0.18)] bg-white text-[var(--ibp-ink)]"
                : "bg-[var(--ibp-gold-500)] text-[var(--ibp-ink)]",
            ].join(" ")}
          >
            {complete ? <Flag className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
            {complete ? "Mark incomplete" : "Mark lesson complete"}
          </button>
        </div>
        {nextLesson ? (
          <Link
            to={`/study/learn/${nextLesson.id}`}
            className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[var(--ibp-navy-950)] hover:underline"
          >
            Next: {nextLesson.title} <ArrowRight className="h-4 w-4" />
          </Link>
        ) : null}
      </section>

      <p className="border-t border-[color:rgba(10,27,51,0.12)] pt-6 text-xs leading-5 text-[color:rgba(11,18,32,0.5)]">
        This lesson is study guidance, not legal advice. Illinois rules can change; use the linked source and official licensing materials to confirm current requirements.
      </p>
    </StudyShell>
  );
}
